import React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { createFilterOptions } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton } from '@mui/material';
import { UserContext } from 'src/store/ContextStore';
import { CONSTANTS } from 'src/components/Constants';

const baseFilter = createFilterOptions();

export default function AutocompleteInput(props) {
  const {
    highlightErrorBorder,
    helperText,
    options = [],
    filterOptions: customFilterOptions,
    onDelete,
    restrictCreate,
    // Given to the input itself. Left in `rest` it landed on MUI's root <div>,
    // where no one sees it, so no autocomplete placeholder ever showed
    // (TGCLOUD-1846).
    placeholder,
    ...rest
  } = props;

  const {
    user: { userType }
  } = React.useContext(UserContext);

  const canCreate = restrictCreate
    ? userType === CONSTANTS.userTypes.ADMIN ||
      userType === CONSTANTS.userTypes.GENETICS_SERVICE_ADMIN
    : userType === CONSTANTS.userTypes.ADMIN ||
      userType === CONSTANTS.userTypes.GENETICS ||
      userType === CONSTANTS.userTypes.GENETICS_SERVICE_ADMIN;

  const filterOptions = (opts, params) => {
    const filtered = customFilterOptions
      ? [...customFilterOptions(opts, params)]
      : baseFilter(opts, params);

    if (canCreate && params.inputValue !== '') {
      filtered.push({
        inputValue: params.inputValue,
        label: `Add "${params.inputValue}"`
      });
    }
    return filtered;
  };

  const getLabel = (option) => {
    if (typeof option === 'string') return option;
    if (option.label) return option.label;
    if (option.inputValue) return option.inputValue;
    return option.label;
  };

  // A PICKER MUST ALWAYS BE ABLE TO OFFER WHAT IT IS CURRENTLY SHOWING
  // (TGCLOUD-1784).
  //
  // Records carry values that are not in the list. Importers write a
  // spreadsheet cell verbatim, the retired Calendly sync wrote its own, three
  // writers put a form's title in the consent type field, and now that
  // drop-down values can be switched off, any value an administrator retires
  // is still on every record that used it.
  //
  // Two things go wrong when the current value is missing from `options`. MUI
  // warns and cannot match the value to an option, and the guard below for a
  // non-creating role refuses to pass a selection back with no callback and no
  // error at all, so a genetics user who opens the field and picks the same
  // value again simply cannot save the record.
  //
  // Injecting it here rather than at twenty-one call sites means no picker can
  // forget. It can only ADD an option that is already on screen, and only when
  // the list does not already hold it.
  const sameLabel = (a, b) =>
    String(a ?? '')
      .trim()
      .toLowerCase() ===
    String(b ?? '')
      .trim()
      .toLowerCase();

  const optionsWithCurrent = React.useMemo(() => {
    const current = props.value;
    if (current === undefined || current === null || current === '') {
      return options;
    }

    const held = (candidate) => {
      const label = getLabel(candidate);
      if (!label) return true;
      return options.some((opt) => sameLabel(getLabel(opt), label));
    };

    // Multiple mode: the value is the whole selected array.
    if (Array.isArray(current)) {
      const missing = current.filter((entry) => entry && !held(entry));
      return missing.length ? [...options, ...missing] : options;
    }

    return held(current) ? options : [...options, current];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, props.value]);

  const error = props.validated && !props.value;

  const onChange = (event, newValue) => {
    event.preventDefault();
    event.stopPropagation();

    if (!canCreate) {
      // In multiple mode newValue is the full selected array — pass it through
      // directly since freeSolo=false already prevents arbitrary free-text input.
      if (Array.isArray(newValue)) {
        return props.onChange(newValue);
      }
      const label = getLabel(newValue || '');
      // Against the list INCLUDING the current value, and matched the same way
      // the rest of this feature matches a label. A strict `===` here refused a
      // value whose spelling differed only in case, silently.
      const exists = optionsWithCurrent.some((opt) =>
        sameLabel(getLabel(opt), label)
      );
      if (!exists) return;
      return props.onChange(newValue);
    }

    if (typeof newValue === 'string') {
      props.onChange({
        label: newValue
      });
    } else if (newValue && newValue.inputValue) {
      // Create a new value from the user input
      props.onChange({
        label: newValue.inputValue
      });
    } else {
      props.onChange(newValue);
    }
  };

  const renderOption =
    onDelete && canCreate
      ? (optionProps, option) => (
          <Box
            component="li"
            {...optionProps}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%'
            }}
          >
            <span style={{ flex: 1 }}>{getLabel(option)}</span>
            {!option.inputValue && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(option);
                }}
                sx={{ ml: 1, p: 0.5 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        )
      : undefined;

  return (
    <Autocomplete
      {...rest}
      options={optionsWithCurrent}
      getOptionLabel={getLabel}
      filterOptions={filterOptions}
      onChange={onChange}
      disableClearable
      forcePopupIcon
      popupIcon={<ArrowDropDownIcon />}
      freeSolo={canCreate}
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      renderOption={renderOption}
      renderInput={(params) => (
        <TextField
          {...params}
          label={undefined}
          placeholder={placeholder}
          className={props.className}
          variant="outlined"
          fullWidth
          error={highlightErrorBorder || error}
          helperText={error && helperText}
        />
      )}
      validated={undefined}
    />
  );
}
